import Tesseract from 'tesseract.js';

// Extract data from receipt image
export const extractReceiptData = async (imageFile, onProgress) => {
    try {
        // Basic check for extremely small images (prevents Tesseract scaling errors)
        if (imageFile instanceof File || imageFile instanceof Blob) {
           // We could check dimensions here if needed, but for now we trust the file
           // unless it's obviously too small in bytes
           if (imageFile.size < 10) throw new Error('Image file is too small');
        }

        const { data: { text } } = await Tesseract.recognize(
            imageFile,
            'eng',
            {
                logger: (m) => {
                    if (onProgress && m.status === 'recognizing text') {
                        onProgress(Math.round(m.progress * 100));
                    }
                }
            }
        );

        return {
            merchant: parseMerchant(text),
            amount: parseAmount(text),
            date: parseDate(text),
            currency: parseCurrency(text),
            rawText: text
        };
    } catch (error) {
        console.error('OCR Error:', error);
        throw error;
    }
};

// Parse merchant name from text
const parseMerchant = (text) => {
    // Get first non-empty line (usually merchant name)
    const lines = text.split('\n').filter(line => line.trim().length > 0);
    if (lines.length > 0) {
        // Return first line, cleaned up
        return lines[0].trim().substring(0, 50);
    }
    return '';
};

// Parse amount from text
const parseAmount = (text) => {
    // Look for patterns like $123.45, 123.45, ₹123.45, 1,234.56
    const patterns = [
        /[₹\$£€]\s*(\d[\d,.]*(?:\d{2})?)/g,  // Currency symbol + amount
        /total[:\s]*[₹\$£€]?\s*(\d[\d,.]*(?:\d{2})?)/gi,  // Total: amount
        /amount[:\s]*[₹\$£€]?\s*(\d[\d,.]*(?:\d{2})?)/gi,  // Amount: amount
        /grand\s*total[:\s]*[₹\$£€]?\s*(\d[\d,.]*(?:\d{2})?)/gi,  // Grand Total: amount
        /\b(\d[\d,.]*\.\d{2})\b/g, // Any 123.45 pattern
        /\b(\d[\d,.]*,\d{2})\b/g   // Any 123,45 pattern
    ];

    let amounts = [];

    for (const pattern of patterns) {
        const matches = [...text.matchAll(pattern)];
        for (const m of matches) {
            let val = m[1];
            // If it's something like 1,234.56 or 1.234,56
            // We need to determine which one is the decimal separator
            // Usually the last one is the decimal separator if it has 2 digits after it

            // Basic cleanup: remove all but the last period/comma
            const lastDot = val.lastIndexOf('.');
            const lastComma = val.lastIndexOf(',');
            const lastSeparator = Math.max(lastDot, lastComma);

            if (lastSeparator !== -1 && val.length - lastSeparator <= 3) {
                // The last separator is likely the decimal separator
                const integerPart = val.substring(0, lastSeparator).replace(/[.,]/g, '');
                const decimalPart = val.substring(lastSeparator + 1);
                const cleaned = `${integerPart}.${decimalPart}`;
                const parsed = parseFloat(cleaned);
                if (!isNaN(parsed)) amounts.push(parsed);
            } else {
                // No separator or it's a thousands separator
                const cleaned = val.replace(/[.,]/g, '');
                const parsed = parseFloat(cleaned);
                if (!isNaN(parsed)) amounts.push(parsed);
            }
        }
    }

    if (amounts.length > 0) {
        // Return the largest amount found (likely the total)
        return Math.max(...amounts).toFixed(2);
    }

    return '';
};

// Parse currency from text
const parseCurrency = (text) => {
    // Convert to uppercase for easier matching
    const upperText = text.toUpperCase();

    // Look for INR indicators (most common in India)
    if (
        text.includes('₹') ||
        /\bINR\b/i.test(text) ||
        /\bRS\.?\s*\d/i.test(text) ||  // Rs. 123 or Rs 123
        /\bRUPEES?\b/i.test(text) ||
        upperText.includes('INDIAN RUPEE') ||
        /\bGSTIN\b/i.test(text) ||
        /\bTAX\s*INVOICE\b/i.test(text)
    ) {
        return 'INR';
    }

    // Look for USD indicators
    if (
        text.includes('$') ||
        /\bUSD\b/i.test(text) ||
        /\bDOLLARS?\b/i.test(text)
    ) {
        return 'USD';
    }

    // Look for EUR indicators
    if (
        text.includes('€') ||
        /\bEUR\b/i.test(text) ||
        /\bEUROS?\b/i.test(text)
    ) {
        return 'EUR';
    }

    // Look for GBP indicators
    if (
        text.includes('£') ||
        /\bGBP\b/i.test(text) ||
        /\bPOUNDS?\b/i.test(text)
    ) {
        return 'GBP';
    }

    // Default to INR if we see common Indian patterns
    if (
        /\b(GSTIN|GST|PAN)\b/i.test(text) ||  // Indian tax identifiers
        /\b(BANGALORE|MUMBAI|DELHI|CHENNAI|KOLKATA|HYDERABAD|PUNE|GURGAON|NOIDA)\b/i.test(text)  // Indian cities
    ) {
        return 'INR';
    }

    // Final fallback to USD
    return 'USD';
};

// Parse date from text
const parseDate = (text) => {
    // Common date patterns - prioritize DD/MM/YYYY for Indian receipts
    const patterns = [
        // DD/MM/YYYY or DD-MM-YYYY (Indian format)
        { regex: /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/, format: 'dmy' },
        // DD/MM/YY or DD-MM-YY
        { regex: /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})/, format: 'dmy2' },
        // YYYY-MM-DD (ISO format)
        { regex: /(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/, format: 'ymd' },
        // Month name formats
        { regex: /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{1,2})[\s,]+(\d{4})/i, format: 'mdy' },
        { regex: /(\d{1,2})[\s,]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{4})/i, format: 'dmy_name' }
    ];

    for (const { regex, format } of patterns) {
        const match = text.match(regex);
        if (match) {
            try {
                let day, month, year;

                if (format === 'dmy') {
                    day = parseInt(match[1]);
                    month = parseInt(match[2]);
                    year = parseInt(match[3]);
                } else if (format === 'dmy2') {
                    day = parseInt(match[1]);
                    month = parseInt(match[2]);
                    year = parseInt(match[3]) + 2000; // Assume 20xx
                } else if (format === 'ymd') {
                    year = parseInt(match[1]);
                    month = parseInt(match[2]);
                    day = parseInt(match[3]);
                } else if (format === 'mdy' || format === 'dmy_name') {
                    // Handle month name formats
                    const dateStr = match[0];
                    const date = new Date(dateStr);
                    if (!isNaN(date.getTime())) {
                        return date.toISOString().split('T')[0];
                    }
                    continue;
                }

                // Check for common American MM/DD/YYYY if format is DMY
                // e.g. 05/20/2024 - clearly 20 is the day, so 05 is month
                if (day > 12 && month <= 12) {
                    // Already correct as DMY
                } else if (day <= 12 && month > 12) {
                    // This is likely MDY
                    const temp = day;
                    day = month;
                    month = temp;
                }

                // Validate day and month
                if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                    // Create date in YYYY-MM-DD format
                    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const date = new Date(dateStr);
                    if (!isNaN(date.getTime())) {
                        return dateStr;
                    }
                }
            } catch (e) {
                continue;
            }
        }
    }

    // Default to today if no date found
    return new Date().toISOString().split('T')[0];
};

// Convert image file to data URL for storage
export const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};
